import './searchPreviewAndDetails.scss';
import React, {Fragment, memo, useEffect, useState} from 'react';
import {
    useSearchPageType,
    useSelectedDocContext,
    useStateChat,
    useStateDocsCount,
    useStateQueryPending,
    useStateResultDetailsTabSize,
} from '../search-hooks';
import {useTranslation} from 'react-i18next';
import {Icons} from '../../icons/icons';
import {SvgIcon} from '../../icons/svgIcon';
import {DrawerProps, HibuDrawer} from '../../common/drawer/hibuDrawer';
import {SearchPreview} from './search-preview/searchPreview';
import {SearchResultDetails} from './search-result-details/searchResultDetails';
import {SearchAssistantView} from '../../assistant/search-assistant/searchAssistantView';
import {ActiveDrawerContent} from '../search-types';
import {changeResultDetailsTabSizeAction, startSearchChatAction} from '../duck/search-actions';
import {useDispatch} from 'react-redux';
import {DragableDivider, DynamicSize} from '../../common/dragable-divider/dragableDivider';
import classNames from 'clsx';

export const SearchPreviewAndDetails = memo(() => {
    const {t} = useTranslation();
    const selectedDoc = useSelectedDocContext();
    const docsCount = useStateDocsCount();
    const queryPending = useStateQueryPending();
    const [showPreviewAndDetails, setShowPreviewAndDetails] = useState(false);
    const activeDrawerContent: ActiveDrawerContent = useStateChat();
    const dispatch = useDispatch();
    const searchPageType = useSearchPageType();
    const [size, setSize] = useState<DynamicSize>(useStateResultDetailsTabSize());

    useEffect(() => {
        setShowPreviewAndDetails(activeDrawerContent !== ActiveDrawerContent.PLACEHOLDER); // toggling drawer on item click
    }, [activeDrawerContent]);

    const isNewQuery = () => {
        return queryPending && docsCount === 0;
    };

    const closeDrawer = () => {
        if (activeDrawerContent === ActiveDrawerContent.ASSISTANT) {
            dispatch(startSearchChatAction(searchPageType, false, ActiveDrawerContent.ASSISTANT));
            return;
        }
        return setShowPreviewAndDetails(false);
    };

    const changeSize = (x: number | undefined, y: number | undefined) => {
        setSize({x: x, y: y});
        dispatch(changeResultDetailsTabSizeAction({x: x, y: y}));
    };

    const content = () => {
        switch (activeDrawerContent) {
            case ActiveDrawerContent.PREVIEW:
                return (
                    <div id={'search-preview-and-details'}>
                        <div className="details-body">
                            <SearchPreview></SearchPreview>
                            <DragableDivider onDrag={changeSize} size={size} revertDirection={true}/>
                            <SearchResultDetails></SearchResultDetails>
                        </div>
                    </div>
                );

            case ActiveDrawerContent.PLACEHOLDER:
                return (
                    <div id={'search-preview-and-details'}>
                        <SearchResultDetails></SearchResultDetails>
                    </div>
                );

            case ActiveDrawerContent.ASSISTANT:
                return (
                    <div id={'search-preview-and-details'}>
                        <SearchAssistantView onCloseRequest={closeDrawer}></SearchAssistantView>
                    </div>
                );

            default:
                return (
                    <div id={'search-preview-and-details'}>
                        <p>{t('search.details.placeholder')}</p>
                    </div>
                );
        }
    };

    const mobileHeaderCallback = ({onCloseRequest}: DrawerProps) => {
        switch (activeDrawerContent) {
            case ActiveDrawerContent.ASSISTANT:
                return (
                    <></>
                );

            default:
                return (
                    <SvgIcon
                        className="details-header-close"
                        icon={Icons.X}
                        onClick={onCloseRequest}
                        alt={t('general.clear')}
                    />
                );
        }
    };

    return (
        <>
            {!isNewQuery() && (
                <HibuDrawer
                    id={'hibu_result_detail_column'}
                    className={classNames(
                        selectedDoc || activeDrawerContent === ActiveDrawerContent.ASSISTANT
                            ? ''
                            : 'no-document-selected',
                    )}
                    show={showPreviewAndDetails}
                    onCloseRequest={closeDrawer}
                    headerCbMobile={mobileHeaderCallback}
                    contentCb={content}
                />
            )}
        </>
    );
});
